import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import { desktopAdminNavGroups, isDesktopAdminRouteActive } from "./desktopAdminNavigation";

const GROUPS_KEY = "transportmate-desktop-nav-groups";
const COLLAPSED_KEY = "transportmate-desktop-nav-collapsed";

function getSavedGroups() {
  try {
    const saved = JSON.parse(localStorage.getItem(GROUPS_KEY) || "[]");
    return Array.isArray(saved) ? saved.filter((key): key is string => typeof key === "string") : [];
  } catch {
    return [];
  }
}

interface DesktopAdminSidebarProps {
  onLogout: () => void;
}

export function DesktopAdminSidebar({ onLogout }: DesktopAdminSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeGroup = desktopAdminNavGroups.find((group) =>
    group.items.some((item) => isDesktopAdminRouteActive(location.pathname, item.path)),
  )?.key;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === "true");
  const [openGroups, setOpenGroups] = useState<string[]>(() => {
    const saved = getSavedGroups();
    return saved.length ? saved : desktopAdminNavGroups.map((group) => group.key);
  });

  useEffect(() => {
    if (activeGroup) setOpenGroups((current) => current.includes(activeGroup) ? current : [...current, activeGroup]);
  }, [activeGroup]);

  useEffect(() => {
    localStorage.setItem(GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      localStorage.setItem(COLLAPSED_KEY, String(!current));
      return !current;
    });
  };

  const toggleGroup = (key: string) => {
    setOpenGroups((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  };

  return (
    <div
      className={cn(
        "flex h-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out",
        collapsed ? "w-16" : "w-56"
      )}
    >
      <div className={cn("flex h-12 items-center border-b border-sidebar-border px-3", collapsed ? "justify-center" : "justify-between")}>
        {!collapsed && <span className="text-[11px] font-semibold uppercase text-muted-foreground">Workspace</span>}
        <Button
          variant="ghost"
          size="icon"
          className="h-9 min-h-9 w-9 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      <ScrollArea className="flex-1 px-2 py-3">
        <div className="space-y-2">
          {desktopAdminNavGroups.map((group) => {
            const isOpen = collapsed || openGroups.includes(group.key);
            return (
              <Collapsible key={group.key} open={isOpen} onOpenChange={() => !collapsed && toggleGroup(group.key)}>
                {!collapsed && (
                  <CollapsibleTrigger className="flex min-h-9 w-full items-center justify-between rounded-md px-2.5 text-[11px] font-semibold uppercase text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground">
                    <span>{group.title}</span>
                    <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")} />
                  </CollapsibleTrigger>
                )}
                <CollapsibleContent className="space-y-1 pt-1 data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                  {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = isDesktopAdminRouteActive(location.pathname, item.path);
                  const navButton = (
                    <Button key={item.id} variant="ghost" className={cn(
                      "relative h-10 w-full justify-start gap-3 px-3 text-[13px] font-medium text-sidebar-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                      collapsed && "justify-center px-0",
                      isActive && "bg-primary/10 text-primary before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-primary hover:bg-primary/10",
                    )} onClick={() => navigate(item.path)} aria-label={collapsed ? item.label : undefined} aria-current={isActive ? "page" : undefined}>
                      <Icon className="h-[18px] w-[18px] shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </Button>
                  );
                  return collapsed ? (
                    <Tooltip key={item.id} delayDuration={150}>
                      <TooltipTrigger asChild>{navButton}</TooltipTrigger>
                      <TooltipContent side="right">{item.label}</TooltipContent>
                    </Tooltip>
                  ) : navButton;
                })}
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      </ScrollArea>

      <div className="border-t border-sidebar-border p-2">
        <Button
          variant="ghost"
          className={cn(
            "h-10 w-full justify-start gap-3 px-3 text-[13px] text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground",
            collapsed && "justify-center px-0"
          )}
          onClick={onLogout}
          aria-label={collapsed ? "Log out" : undefined}
        >
          <LogOut className="h-[18px] w-[18px]" />
          {!collapsed && <span>Log Out</span>}
        </Button>
      </div>
    </div>
  );
}
